import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import FacebookRefresh from './subcomponents/properties/facebook-refresh.component';
import Filters from './subcomponents/filters';
import Sources from './subcomponents/sources';
import Posts from './subcomponents/posts';

import HttpSocialService from './http-social-service';
import * as helpers from './helpers/dropdown-feed.helper';

import {
	extractActiveFeeds,
	chooseSelectedFeed,
	remapPosts,
	buildNotificationsAndPosts,
	FeedSourceOption,
} from './helpers/dropdown-feed.helper';

import {
	returnObjectForSelectedFeed,
	returnObjectForSelectedDate,
	returnObjectForSelectedFilterSources,
	returnObjectForSourceTabUpdate,
	returnObjectForUpdateNotifications,
} from '../../../store/action-creators/feeds-action-creator';

import { analyticsService } from '../../../App';
import Post from '../../../models/feed/post/post';
import Feed from '../../../models/feed/feed';
import Project from '../../../models/project/Project';
import Profile from '../../../models/profile/Profile';

import './styles/container-style.scss';
import { IDateFilterOption, ISourceTab } from './models/models';

// Constants
const POLLING_INTERVAL_MS = 300000;

export type Properties = {
	currentProject: Project;
	profile: Profile;
	updateSelectedFeed: (feed: Feed) => void;
	updateSelectedDate: (date: IDateFilterOption) => void;
	updateNotifications: (notifications: Record<string, any>[]) => void;
	sourceTabUpdate: (sourceTab: ISourceTab) => void;
	updateSelectedFilterSources: (selectedSources: FeedSourceOption[]) => void;
	selectedFeed: Feed;
	selectedDate: IDateFilterOption;
	notifications: Record<string, any>[];
	selectedFilterSources: FeedSourceOption[];
	sourceTab: ISourceTab;
};

const SocialFeedContainer: React.FC<Properties> = ({
	currentProject,
	profile,
	selectedFeed,
	selectedDate,
	selectedFilterSources,
	sourceTab,
	notifications,
	updateSelectedFeed,
	updateSelectedDate,
	updateNotifications,
	sourceTabUpdate,
	updateSelectedFilterSources,
}) => {
	const [t] = useTranslation();
	const [feeds, setFeeds] = useState<Feed[]>([]);
	const [isContentLoading, setIsContentLoading] = useState(false);
	const [selectFeedValue, setSelectFeedValue] = useState<Record<string, any>>({});
	const [collapsed, setCollapsed] = useState(true);
	const [posts, setPosts] = useState<Post[]>([]);
	const [newPosts, setNewPosts] = useState<Post[]>([]);
	const [sourcePosts, setSourcePosts] = useState<Post[]>([]);

	const containerRef = useRef<HTMLDivElement>(null);
	const collapsedRef = useRef<boolean>(collapsed);
	const intervalRef = useRef<number>(-1);

	const feedsRequest = useCallback(async () => {
		try {
			setIsContentLoading(true);
			const result = await HttpSocialService.requestFeeds(currentProject.domain, profile.id);
			const activeFeeds = extractActiveFeeds(result.data.data);
			const selected = chooseSelectedFeed(activeFeeds, '');

			clearInterval(intervalRef.current);

			sourcesRequest(selected.id);
			updateSelectedFeed(selected);
			setSelectFeedValue(helpers.feedToOption(selected));
			setFeeds(activeFeeds);
			setIsContentLoading(activeFeeds.length > 0);
		} catch (error) {
			console.error('Error fetching feeds', error);
			setIsContentLoading(false);
		}
	}, [currentProject.domain, profile.id, updateSelectedFeed]);

	const sourcesRequest = async (feedId: string) => {
		if (feedId === '-1') {
			setPosts([]);
			updateNotifications([]);
			clearInterval(intervalRef.current);
			return;
		}
		const result = await HttpSocialService.requestFeedsSources(currentProject.domain, profile.id, feedId);
		updateSelectedFeed(result.data.data);
		fetchInitialData(result.data.data);
	};

	const fetchInitialData = (feed: Feed) => {
		const defaultSources = selectedFilterSources.length > 0 ? selectedFilterSources : helpers.extractTypeFilterOptions(feed.sources);
		const hasDate = selectedDate && selectedDate.value;
		const defaultDate = helpers.dateFilterOptions(t)[0];

		if (defaultSources.length > 0) {
			sourcePostsRequest('All', feed.id, hasDate ? selectedDate.value : defaultDate.value, defaultSources);
			clearInterval(intervalRef.current);
			intervalRef.current = window.setInterval(() => {
				newPostsRequest(feed.id, '5m', defaultSources);
			}, POLLING_INTERVAL_MS);
			updateSelectedFilterSources(defaultSources);
			setSelectFeedValue(helpers.feedToOption(feed));
			setIsContentLoading(true);
		}
	};

	const newPostsRequest = useCallback(
		async (feedId: string, period: string, selectedSources: FeedSourceOption[]) => {
			try {
				const types = selectedSources.map((source) => source.value);
				const result = await HttpSocialService.requestPostsByTypePeriod(currentProject.domain, profile.id, feedId, period, types.join(','));
				const mappedPosts = remapPosts(result.data.data, selectFeedValue.feed && selectFeedValue.feed.sources);
				const resultPosts = buildNotificationsAndPosts(mappedPosts, posts, selectedFeed && selectedFeed.sources, sourceTab.location);

				updateNotifications(resultPosts.notifications);
				setNewPosts(mappedPosts);
				setPosts(resultPosts.allPosts);
				setSourcePosts(resultPosts.sourceSpecificPosts);
			} catch (error) {
				console.error('Error fetching new posts', error);
				setNewPosts([]);
			}
		},
		[currentProject.domain, profile.id, posts, selectedFeed, selectFeedValue, sourceTab, updateNotifications],
	);

	const sourcePostsRequest = useCallback(
		async (sourceId: string, feedId: string, date: string, selectedSources: FeedSourceOption[]) => {
			try {
				setIsContentLoading(true);

				if (sourceId === 'All' && selectedSources.length > 0) {
					const types = selectedSources.map((source) => source.value);
					const result = await HttpSocialService.requestPostsByTypePeriod(currentProject.domain, profile.id, feedId, date, types.join(','));
					setPosts(remapPosts(result.data.data, selectFeedValue.feed && selectFeedValue.feed.sources));
				} else if (sourceId !== 'All') {
					const result = await HttpSocialService.requestPostsBySourcePeriod(currentProject.domain, profile.id, feedId, date, sourceId);
					setSourcePosts(remapPosts(result.data.data, selectFeedValue.feed && selectFeedValue.feed.sources));
				} else {
					setPosts([]);
				}
			} catch (error) {
				console.error('Error fetching posts', error);
				setPosts([]);
				setSourcePosts([]);
			} finally {
				setIsContentLoading(false);
			}
		},
		[currentProject.domain, profile.id, selectFeedValue.feed],
	);

	const toggleSidebar = function () {
		analyticsService.sendEvent('Click', 'News tracker', collapsed ? 'Open' : 'Close');

		setCollapsed(function (prev) {
			const nextCollapsed = !prev;
			const mainElement = document.querySelector('main.main') as HTMLElement;

			if (mainElement) {
				// ONLY apply margin when sidebar is collapsed
				mainElement.style.marginRight = nextCollapsed ? '52px' : '0px';
			}

			return nextCollapsed;
		});
	};

	const onFeedChange = useCallback(
		(feed: any) => {
			const hasDate = selectedDate && selectedDate.value;
			const defaultDate = helpers.dateFilterOptions(t)[0];

			sourcesRequest(feed.feed.id);
			sourcePostsRequest('All', feed.feed.id, hasDate ? selectedDate.value : defaultDate.value, selectedFilterSources);
			updateNotifications([]);
			sourceTabUpdate({ id: 'All', location: '' });
			const defaultSources = helpers.extractTypeFilterOptions(feed.feed.sources);
			updateSelectedFilterSources(defaultSources);
			setSelectFeedValue(helpers.feedToOption(feed.feed));
			setNewPosts([]);
			setIsContentLoading(true);
		},
		[selectedDate, selectedFilterSources, updateNotifications, updateSelectedFilterSources, sourceTabUpdate],
	);

	const onSourceChange = useCallback(
		(sourceId: string, sourceLocation: string) => {
			const hasDate = selectedDate && selectedDate.value;
			const defaultDate = helpers.dateFilterOptions(t)[0];

			const filteredNotifications = notifications.filter((item) => item.sourceLocation !== sourceLocation);
			updateNotifications(filteredNotifications);
			sourceTabUpdate({ id: sourceId, location: sourceLocation });
			sourcePostsRequest(sourceId, selectedFeed.id, hasDate ? selectedDate.value : defaultDate.value, selectedFilterSources);

			if (collapsed) toggleSidebar();
		},
		[collapsed, notifications, selectedFeed, selectedDate, selectedFilterSources, sourceTabUpdate, updateNotifications],
	);

	const clearFilters = useCallback(
		(source: string, id: string) => {
			const defaultSources = helpers.extractTypeFilterOptions(selectedFeed.sources);
			const defaultDate = helpers.dateFilterOptions(t)[0];
			updateSelectedDate(defaultDate);
			updateSelectedFilterSources(defaultSources);
			sourcePostsRequest(source, id, defaultDate.value, defaultSources);
		},
		[selectedFeed, t, updateSelectedDate, updateSelectedFilterSources, sourcePostsRequest],
	);

	// Use Effects
	useEffect(() => {
		const defaultDate = helpers.dateFilterOptions(t)[0];

		if (!selectedDate || !selectedDate.value) {
			updateSelectedDate(defaultDate);
		}
	}, [selectedDate, updateSelectedDate]);

	useEffect(() => {
		feedsRequest();
	}, [feedsRequest]);

	useEffect(() => {
		collapsedRef.current = collapsed;
	}, [collapsed]);

	useEffect(
		function () {
			const mainElement = document.querySelector('main.main') as HTMLElement;
			if (mainElement) {
				mainElement.style.marginRight = collapsed ? '52px' : '0px';
			}
		},
		[collapsed],
	);

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				if (!collapsedRef.current) {
					setCollapsed(true);
					analyticsService.sendEvent('Click', 'News tracker', 'Close');
				}
			}
		}

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<div ref={containerRef} className={`feed-container ${collapsed ? 'collapsed' : 'shown'}`}>
			<div className='sources-sidebar'>
				<div className='scroll-container sources-scrollbar'>
					<Sources
						selectedFeed={selectedFeed}
						sourceTab={(sourceTab && sourceTab.id) || ''}
						toggleSidebar={toggleSidebar}
						onSourceChange={onSourceChange}
						collapsed={collapsed}
						notifications={notifications}
					/>
				</div>
			</div>
			<div className='content-sidebar'>
				<div className='scroll-container scrollbar-wrapper'>
					<div className='d-flex flex-column w-100 pr-1'>
						<div className='d-flex justify-center p-2'>
							<Select value={selectFeedValue} options={helpers.remapFeedsToOptions(feeds)} onChange={onFeedChange} className='w-100' />
						</div>
						<div className='d-flex flex-column w-100 p-1'>
							<Filters
								t={t}
								onChangeDate={updateSelectedDate}
								onChangeType={updateSelectedFilterSources}
								clearFilters={clearFilters}
								sourceTab={(sourceTab && sourceTab.id) || ''}
								selectedFeedId={(sourceTab && selectedFeed.id) || ''}
								dateFilterOptions={helpers.dateFilterOptions}
								selectedDate={selectedDate}
								selectedFilterSources={selectedFilterSources}
								feedSources={helpers.extractTypeFilterOptions((selectedFeed && selectedFeed.sources) || [])}
								sourcePostsRequest={sourcePostsRequest}
							/>
							<div>
								<Posts posts={sourceTab && sourceTab.id === 'All' ? posts : sourcePosts} t={t} profile={profile} />
							</div>
						</div>
					</div>
				</div>
				<FacebookRefresh />
			</div>
		</div>
	);
};

function mapStateToProps(state: any) {
	return {
		selectedFeed: state.feed.selectedFeed,
		notifications: state.feed.notifications,
		selectedDate: state.feed.selectedDate,
		sourceTab: state.feed.sourceTab,
		selectedFilterSources: state.feed.selectedFilterSources,
	};
}

function mapDispatchToProps(dispatch: any) {
	return {
		updateSelectedFeed: (feed: Feed) => dispatch(returnObjectForSelectedFeed(feed)),
		updateSelectedDate: (date: IDateFilterOption) => dispatch(returnObjectForSelectedDate(date)),
		sourceTabUpdate: (sourceTab: ISourceTab) => dispatch(returnObjectForSourceTabUpdate(sourceTab)),
		updateSelectedFilterSources: (sources: FeedSourceOption[]) => dispatch(returnObjectForSelectedFilterSources(sources)),
		updateNotifications: (notifications: any[]) => dispatch(returnObjectForUpdateNotifications(notifications)),
	};
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(SocialFeedContainer);
